import React, { useState } from 'react';
import { useForm, useSubmit } from '@balance-web/forms';

import { WidgetContainer } from '../../generic/wigetContainer';
import type { ErrorResponseType } from '../../useErrorResponses';
import type { GoogleSignupProps } from '../../../lib/api';
// import { checkExistingEmail } from '../../../lib/api'; // TODO: Bring me back if needed.
import type { SignupInput } from '../../../services/queryClient/useMutateSignup';
import type { Country } from '../../../types/user';
import { useMutateMicrosoftLogin } from '../../../services/queryClient/useMutateMicrosoftLogin';
import { useAuth } from '../../../context/useAuth';
import { useRedirectToClient } from '../useRedirectToClient';
import { useMicrosoftAuthentication } from '../../hooks/useMicrosoftAuthentication';
import { useGoogleAuthentication } from '../../hooks/useGoogleAuthentication';
import { useMutateGoogleSignup } from '../../../services/queryClient/useMutateGoogleSignup';
import { events, track } from '../../../utils/analytics';

import { SingupFormSchema } from './formSchema';
import { SignUpForm } from './form';

export type SignUpProps = {
  handleSignUp: (formData: SignupInput) => void;
  submitting?: boolean;
  onLoginClick: () => void;
  error?: ErrorResponseType;
  message?: string;
  email?: string;
  inviteId?: string;
  goToCheckoutCallback?: () => void;
};

export const SignUp: React.FC<SignUpProps> = ({
  submitting,
  handleSignUp,
  onLoginClick,
  error,
  email,
  message,
  inviteId,
  goToCheckoutCallback,
}) => {
  const [signupError, setSignupError] = useState('');
  const microsoftSignup = useMutateMicrosoftLogin(); //change when BE tenanct bug is fixed
  const googleSignup = useMutateGoogleSignup();
  const { redirectToClient } = useRedirectToClient();
  const forMarketing = goToCheckoutCallback ? true : false;

  const handleGoogleSignUp = async (data: GoogleSignupProps) => {
    try {
      await googleSignup.mutate(data, {
        onSuccess: (user) => {
          setUser(user);
          if (goToCheckoutCallback) {
            localStorage.verified = user.login;
            localStorage.token = user.token;
            localStorage.userid = user.userid;
            localStorage.region = localStorage.region || user.region || 'AU';
            localStorage.username = email;
            localStorage.email = email;
            goToCheckoutCallback();
          } else {
            redirectToClient(data.redirectURL, user.token);
          }

          track({
            action: events.USER_LOGGED_IN,
            params: {
              user_id: user.userid,
              source: 'Identity',
            },
          });
        },
        onError: (err: any) => {
          setSignupError(err.message || 'An error occurred during signup.');
          window.scrollTo(0, 0);
        },
      });
    } catch (e) {}
  };

  const { googleLogin } = useGoogleAuthentication(handleGoogleSignUp);
  const { setUser } = useAuth();

  const form = useForm(SingupFormSchema, {
    country: forMarketing ? localStorage.region : 'AU',
    ...(inviteId && email ? { email } : {}),
  });

  const handleMicrosoftSignup = async (
    err: any,
    token: string,
    ADAL: any,
    redirectUrl: string
  ) => {
    if (!err) {
      try {
        var user = ADAL!.getCachedUser();
        var email = user.profile.unique_name;
        await microsoftSignup.mutate(
          {
            token,
            email,
          },
          {
            onSuccess: (data) => {
              setUser(data);
              if (goToCheckoutCallback) {
                localStorage.verified = data.login;
                localStorage.token = data.token;
                localStorage.userid = data.userid;
                localStorage.region =
                  localStorage.region || data.region || 'AU';
                localStorage.username = email;
                localStorage.email = email;
                goToCheckoutCallback();
              } else {
                redirectToClient(redirectUrl, data.token);
              }
              track({
                action: events.USER_LOGGED_IN,
                params: {
                  user_id: data.userid,
                  source: 'Identity',
                },
              });
            },
            onError: (err: any) => {
              setSignupError(err.message || 'An error occurred during signup.');
              window.scrollTo(0, 0);
            },
          }
        );
      } catch (e) {}
    } else {
      console.error('error: ' + err);
    }
  };
  const { ADAL } = useMicrosoftAuthentication(handleMicrosoftSignup);
  const handleSubmit = useSubmit(form, async (data) => {
    await handleSignUp({
      email: data.email,
      password: data.password,
      phone: data.phonenumber,
      country: data.country as Country,
      lastname: data.lastname,
      firstname: data.firstname,
      recaptcha: data.captchaToken,
    });
  });
  return (
    <WidgetContainer forMarketing={forMarketing}>
      <SignUpForm
        email={email || ''}
        error={error}
        form={form}
        forMarketing={forMarketing}
        inviteId={inviteId || ''}
        message={message || signupError}
        submitting={submitting}
        onLoginClick={onLoginClick}
        onSigninWithGoogleClick={googleLogin}
        onSignInWithMicrosoftClick={() => {
          try {
            if (ADAL) {
              ADAL.login();
            } else {
              throw new Error('Microsoft is not initialized.');
            }
          } catch (error) {
            throw error;
          }
        }}
        onSubmit={handleSubmit}
      />
    </WidgetContainer>
  );
};
